import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { cartContext } from "../../Context/CartContext";
import Loader from "../Loader/Loader";
import axios from "axios";

const Cart = () => {
  const {
    removeItem,
    updateProductCount,
    clearCart,
    getLoggedUserCart,
    setnumOfCartItems,
  } = useContext(cartContext);
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await getLoggedUserCart();
      setCartData(response.data.response.cart);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast("Cart Is Empty 🛒");
      setLoading(false);
    }
  };

  const updateQuantity = async (item, newQuantity) => {
    if (newQuantity < 1) {
      toast.error("Minimum quantity is 1");
      return;
    }

    setActionLoading(true);
    try {
      const response = await updateProductCount(
        item._id,
        newQuantity,
        item.size, // Pass size from item
        item.type === "Custom Product" ? "custom" : "product"
      );
      if (response.status === 200) {
        toast.success("Quantity updated");
        fetchCart();
      }
    } catch (error) {
      toast.error("Failed to update quantity");
    } finally {
      setActionLoading(false);
    }
  };

  const DeleteItem = async (item) => {
    setActionLoading(true);
    try {
      if (item.type === "Custom Product") {
        // Call the custom product API
        const response = await axios.delete(
          `https://api.elza2.store/api/v1/custom/${item._id}`,
          { headers: { Authorization: localStorage.getItem("Auth") } }
        );
        if (response.status !== 200) {
          throw new Error("Failed to process custom product removal");
        }
        toast.success("Custom product removed successfully");
      } else {
        // Proceed with the normal removeItem logic
        const response = await removeItem(item._id, item.size); // Pass size to removeItem
        if (response.status === 200) {
          toast.success("Item removed from cart");
        } else {
          throw new Error("Failed to remove item");
        }
      }
      fetchCart(); // Refresh cart data
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove item");
    } finally {
      setActionLoading(false);
    }
  };

  const clearCartItems = async () => {
    setActionLoading(true);
    try {
      await clearCart();
      setnumOfCartItems(0);
      setCartData(null);
      toast.success("Cart cleared");
    } catch (error) {
      toast.error("Failed to clear cart");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <Loader />;

  if (!cartData || cartData.items.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-lg font-semibold">Your cart is empty</p>
          <Link
            to="/products"
            className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 inline-block mt-4"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const { summary } = cartData;

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Shopping Cart</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Cart Items */}
        <div className="col-span-2 bg-white shadow rounded-lg p-6">
          {cartData.items.map((item) => (
            <CartItemRow
              key={item.id}
              item={item}
              onUpdateQuantity={updateQuantity}
              onRemove={() => DeleteItem(item)}
              actionLoading={actionLoading}
            />
          ))}

          <button
            onClick={clearCartItems}
            disabled={actionLoading}
            className={`bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 mt-4 ${
              actionLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            Clear Cart
          </button>
        </div>

        {/* Summary */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Summary</h2>
          {actionLoading ? (
            <div className="flex justify-center items-center h-32">
              <Loader />
            </div>
          ) : (
            <div className="space-y-2">
              {summary.promotions?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Promotion Details
                  </h3>
                  {summary.promotions.map((promotion, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 mb-4"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          <span className="font-semibold">Category:</span>{" "}
                          {promotion.category}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <p>
                          <span className="font-semibold">Total Quantity:</span>{" "}
                          {promotion.totalQuantity}
                        </p>
                        <p>
                          <span className="font-semibold">Actual Price:</span>{" "}
                          {promotion.actualPrice} EGP
                        </p>
                        <p>
                          <span className="font-semibold">Paid Price:</span>{" "}
                          {promotion.paidPrice} EGP
                        </p>
                        <p className="text-sm text-gray-500 italic">
                          "{promotion.message}"
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <hr className="my-4" />
              <div className="flex justify-between">
                <span>Total Items</span>
                <span>{summary.totalItems} Items</span>
              </div>
              <div className="flex font-bold justify-between">
                <span>Total Price</span>
                <span className="text-main text-xl">
                  {summary.totalPrice} EGP
                </span>
              </div>
            </div>
          )}
          <Link
            to="/checkout"
            className={`bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 mt-4 w-full inline-block text-center ${
              actionLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={actionLoading}
          >
            Checkout Now
          </Link>
        </div>
      </div>
    </div>
  );
};

const CartItemRow = ({ item, onUpdateQuantity, onRemove, actionLoading }) => (
  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b py-4 gap-4 sm:gap-6 last:border-b-0">
    {/* Image and Name */}
    {item.type === "Custom Product" ? (
      <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto cursor-default">
        <img
          src={item.image || "https://via.placeholder.com/150"}
          alt={item.name}
          className="w-16 h-16 object-cover rounded"
        />
        <div>
          <p className="font-semibold">
            {item.name} <span className="text-sm text-gray-500">(Custom)</span>
          </p>
          <p className="text-sm text-gray-500">
            {item.category}
            {item.size && (
              <span className="ml-2 text-gray-400 italic">({item.size})</span>
            )}
          </p>
        </div>
      </div>
    ) : (
      <Link
        to={`/product-details/${item._id}`}
        className="flex items-start sm:items-center space-x-4 w-full sm:w-auto"
      >
        <img
          src={item.image || "https://via.placeholder.com/150"}
          alt={item.name}
          className="w-16 h-16 object-cover rounded"
        />
        <div>
          <p className="font-semibold">{item.name}</p>
          <p className="text-sm text-gray-500">
            {item.category}
            {item.size && (
              <span className="ml-2 text-gray-400 italic">({item.size})</span>
            )}
          </p>
        </div>
      </Link>
    )}

    {/* Quantity Controls */}
    <div className="flex items-center">
      <button
        onClick={() => onUpdateQuantity(item, item.quantity - 1, item.type)}
        className={`bg-gray-200 px-2 rounded ${
          item.type === "Custom Product" || actionLoading
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
      >
        -
      </button>
      <span className="mx-2">{item.quantity}</span>
      <button
        onClick={() => onUpdateQuantity(item, item.quantity + 1, item.type)}
        className={`bg-gray-200 px-2 rounded ${
          item.type === "Custom Product" || actionLoading
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
      >
        +
      </button>
    </div>

    {/* Quantity and Price */}
    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
      {/* Total Price with Unit Price */}
      <div className="flex flex-col items-start">
        <span>{item.totalPrice} EGP</span>
        {item.size && (
          <span className="text-xs text-gray-500">
            {item.unitPrice} EGP per {item.size}
          </span>
        )}
      </div>

      {/* Remove Button */}
      <button
        onClick={() => onRemove(item.id)}
        disabled={actionLoading}
        className={`text-red-500 hover:bg-red-100 px-2 py-1 rounded ${
          actionLoading ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        Remove
      </button>
    </div>
  </div>
);

export default Cart;
